import { useDocEngine } from "@hooks";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useId, useState } from "react";
import type { GroupedToolCall } from "../group-tool-messages";
import type { ChatToolMessage } from "../types";
import { RawToolDetails } from "./RawToolDetails";
import { TOOL_PRESENTATION } from "./tool-call-presentation";
import { ToolCallIcon } from "./ToolCallIcon";
import { ToolStatusChip } from "./ToolStatusChip";
import { useToolCallPresentation } from "./useToolCallPresentation";
import { getMcpServerToolName } from "./utils/tool-calls";

export const OrdinaryToolRow = ({
  call,
  result,
  running,
  decision,
}: {
  call: GroupedToolCall;
  result?: ChatToolMessage;
  running: boolean;
  decision?: "approve" | "reject" | "edit";
}) => {
  const {
    deviceId,
    connections,
    name,
    document,
    documentTool,
    inputTarget,
    label,
    labelWithoutTarget,
    serverName,
    integrationIcon,
    active,
    failed,
    approved,
    rejected,
  } = useToolCallPresentation(call, result, running, decision);
  const { navigateToQMSDocument } = useDocEngine(deviceId);
  const [expanded, setExpanded] = useState(false);
  const panelId = useId();
  const hasRawDetails = ![
    "edit_document",
    "edit_custom_document_text",
    "edit_data_key",
    "write_data_key",
    "task",
    "draft_skill",
    "draft_training_program",
    "draft_training_quiz",
  ].includes(name);
  const Chevron = expanded ? ChevronUp : ChevronDown;

  return (
    <div data-tool-call-id={call.id} className="min-w-0">
      <div className="group/tool-row relative flex min-h-[30px] min-w-0 items-center gap-[10px] py-1 text-[13px] leading-5 text-[#6b7280]">
        {hasRawDetails && (
          <button
            type="button"
            aria-label={label}
            aria-expanded={expanded}
            aria-controls={panelId}
            onClick={() => setExpanded(!expanded)}
            className="peer absolute inset-0 w-full cursor-pointer rounded-[3.5px] border-0 bg-transparent p-0 group-hover/tool-row:bg-black/[.04] focus-visible:bg-black/[.04]"
          />
        )}
        <ToolCallIcon
          name={name}
          integrationIcon={integrationIcon}
          active={active}
          failed={failed}
        />
        <span
          className="pointer-events-none relative min-w-0 flex-1 truncate"
          title={label}
        >
          {document && documentTool ? (
            <>
              {labelWithoutTarget}
              <button
                type="button"
                className="pointer-events-auto relative cursor-pointer border-0 bg-transparent p-0 text-[13px] text-[#074ee8] hover:underline"
                onClick={() =>
                  navigateToQMSDocument({ documentId: document.id })
                }
              >
                {document.title}
              </button>
            </>
          ) : name && !TOOL_PRESENTATION[name] ? (
            <>
              {active ? "Using" : "Used"} {serverName ? `${serverName} ` : ""}
              tool{" "}
              <code className="rounded bg-black/[.04] px-1 text-[12px]">
                {name}
              </code>
            </>
          ) : inputTarget &&
            ["read_data_key", "edit_data_key", "write_data_key"].includes(
              name
            ) ? (
            <>
              {labelWithoutTarget}
              <code className="rounded bg-black/[.04] px-1 text-[12px]">
                {inputTarget}
              </code>
            </>
          ) : (
            label
          )}
        </span>
        {active && (
          <span className="pointer-events-none relative shrink-0 text-xs">
            Running
          </span>
        )}
        {failed && (
          <span className="pointer-events-none relative shrink-0 text-xs text-[#b91c1c]">
            Failed
          </span>
        )}
        {approved && <ToolStatusChip status="Approved" />}
        {rejected && <ToolStatusChip status="Rejected" />}
        {hasRawDetails && (
          <Chevron
            aria-hidden="true"
            className={`pointer-events-none relative h-[14px] w-[14px] shrink-0 text-black/54 ${expanded ? "opacity-100" : "opacity-0 group-hover/tool-row:opacity-100 peer-focus-visible:opacity-100"}`}
          />
        )}
      </div>
      {hasRawDetails && expanded && (
        <div id={panelId}>
          <RawToolDetails
            name={
              TOOL_PRESENTATION[name]
                ? name
                : getMcpServerToolName(name, connections?.connections)
            }
            parameters={call.toolCall.args}
            result={result?.content ?? call.toolCall.error}
            waiting={active && !call.toolCall.error}
          />
        </div>
      )}
    </div>
  );
};
